import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Grid2 as Grid } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';
import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';
import { MultiSelectAutocomplete } from 'src/pages/tickets/components/custom-multiSelect';

import { Form } from 'src/components/hook-form';

import { useChannelContext } from '../hooks';
import { ChannelStepLayout } from '../shared';

export function Step3() {
  const { state: channelState, isLoading, setField, onChangeStep } = useChannelContext();
  const { t } = useTranslate('translation');

  // ------------------- Validation Setup -------------------
  type CampaignField = { assignUsers: any[] };
  const fldList = [{ name: 'assignUsers', required: true }];
  const CampaignSchema = validationSchema(fldList, t);

  const methods = useForm<CampaignField>({
    mode: 'all',
    resolver: zodResolver(CampaignSchema),
    defaultValues: {
      assignUsers: channelState?.assignUsers || [],
    },
  });

  const { handleSubmit, watch, setValue } = methods;
  const watchAssignUsers = watch('assignUsers');

  // ------------------- Fetch Assignee List -------------------
  const {
    _recordList,
    searchValue,
    isFetching,
    open: openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetAssigneeList({
    setValue: setValue as any,
    multiFieldAssign: channelState?.assignUsers ? channelState?.assignUsers : [],
  });

  // ------------------- Handlers -------------------
  const onSubmit = handleSubmit((data) => {
    // ✅ Update context with selected users
    setField('assignUsers', data.assignUsers);
    onChangeStep('next');
  });

  const handleChange = ({ value }: { value: any }) => {
    if (!value) {
      setField('assignUsers', []);
      return;
    }
    setField('assignUsers', value);
  };

  const isNextDisabled = () => !watchAssignUsers || watchAssignUsers.length === 0;

  return (
    <>
      {isLoading && <AuthLoader />}

      <Form methods={methods} onSubmit={onSubmit}>
        <ChannelStepLayout title="" subtitle="" isNextDisabled={isNextDisabled()}>
          <Grid container spacing={3}>
            <MultiSelectAutocomplete
              name="assignUsers"
              label={t('ASSIGNED_TO')}
              placeholder={`+ ${t('RECIPIENTS')}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              options={_recordList}
              searchValue={searchValue}
              listScrollHandler={listScrollHandler}
              searchValueHandler={searchValueHandler}
              open={openList}
              openListHandler={openListHandler}
              closeListHandler={closeListHandler}
              isFetching={isFetching}
              onChangeField={handleChange}
            />
          </Grid>
        </ChannelStepLayout>
      </Form>
    </>
  );
}
